import { useState } from "react"
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip, Typography
} from "@mui/material"
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined"
import { Company } from "../entities/company"
import { CompanyWithTokenRequestsCount } from "../entities/company-with-token-requests-count"

export interface CompaniesTableProps {
    onCompanyClick: (company: Company) => void;
    companies: Company[] | CompanyWithTokenRequestsCount[]
    showTokenCount?: boolean
}

export const CompaniesTable = ({ companies, onCompanyClick, showTokenCount = false }: CompaniesTableProps) => {
    const [order, setOrder] = useState<'asc' | 'desc'>('asc')
    const [orderBy, setOrderBy] = useState<string>('name')

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const sortedCompanies = [...companies].sort((a: any, b: any) => {
        if (a[orderBy] < b[orderBy]) return order === 'asc' ? -1 : 1
        if (a[orderBy] > b[orderBy]) return order === 'asc' ? 1 : -1
        return 0
    })

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={() => handleRequestSort('name')}
                            >
                                Name
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === 'tokenManagerEmail'}
                                direction={orderBy === 'tokenManagerEmail' ? order : 'asc'}
                                onClick={() => handleRequestSort('tokenManagerEmail')}
                            >
                                Token manager
                            </TableSortLabel>
                        </TableCell>
                        {showTokenCount && (
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'pendingTokenRequestCount'}
                                    direction={orderBy === 'pendingTokenRequestCount' ? order : 'asc'}
                                    onClick={() => handleRequestSort('pendingTokenRequestCount')}
                                >
                                    Pending requests
                                </TableSortLabel>
                            </TableCell>
                        )}
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === 'membershipDate'}
                                direction={orderBy === 'membershipDate' ? order : 'asc'}
                                onClick={() => handleRequestSort('renewalDate')}
                            >
                                Membership End Date
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Is a Token Reseller</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedCompanies.map((company: any) => (
                        <TableRow hover key={company.id} onClick={() => onCompanyClick(company)}>
                            <TableCell>{company.name}</TableCell>
                            <TableCell>{company.tokenManagerEmail}</TableCell>
                            {showTokenCount && <TableCell>{company.pendingTokenRequestCount}</TableCell>}
                            <TableCell>{new Date(company.renewalDate).toLocaleString("en-US", { dateStyle: "full" })}</TableCell>
                            <TableCell>{company.tokenReseller ? (<Typography>Yes</Typography>) : (<Typography>No</Typography>)}</TableCell>
                            <TableCell>
                                {company.membershipCanceled ? (
                                    <Tooltip
                                        title={`Canceled member since ${new Date(company.membershipCanceledAt).toLocaleString("en-US", { dateStyle: "full" })}`}
                                    >
                                        <CardMembershipOutlinedIcon color="error" />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Active member">
                                        <CardMembershipOutlinedIcon color="success" />
                                    </Tooltip>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}